import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import Keycloak from 'keycloak-js';
import { apiService } from '../services/apiService';

interface AuthContextType {
  keycloak: Keycloak | null;
  authenticated: boolean;
  initialized: boolean;
  login: () => void;
  logout: () => void;
  token: string | null;
}

const AuthContext = createContext<AuthContextType>({
  keycloak: null,
  authenticated: false,
  initialized: false,
  login: () => {},
  logout: () => {},
  token: null,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const PUBLIC_MODE = (process.env.REACT_APP_PUBLIC_MODE || 'false') === 'true';
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (PUBLIC_MODE) {
      // Skip Keycloak entirely in public mode
      setInitialized(true);
      setAuthenticated(false);
      apiService.setToken(null);
      return;
    }

    const kc = new Keycloak({
      url: process.env.REACT_APP_KEYCLOAK_URL || 'https://auth.askthebill.ai',
      realm: process.env.REACT_APP_KEYCLOAK_REALM || 'askthebill',
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'congress-api',
    });

    // Ensure Login button can work even if init fails
    setKeycloak(kc);

    kc.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html'
    }).then((auth) => {
      setAuthenticated(auth);
      setInitialized(true);

      if (auth) {
        // Immediately set token for API service
        apiService.setToken(kc.token || null);
        // Set up token refresh
        const intervalId = setInterval(() => {
          kc.updateToken(70).then((refreshed) => {
            // Always update apiService token (even if not refreshed, token may have rotated)
            apiService.setToken(kc.token || null);
            if (refreshed) {
              console.log('Token refreshed');
            }
          }).catch(() => {
            console.log('Failed to refresh token');
            kc.login();
          });
        }, 60000);
        // Cleanup on unmount
        return () => clearInterval(intervalId);
      } else {
        apiService.setToken(null);
      }
    }).catch((error) => {
      console.error('Keycloak initialization failed:', error);
      // Keep keycloak instance so manual login can be attempted
      setKeycloak(kc);
      setInitialized(true);
      apiService.setToken(null);
    });
  }, [PUBLIC_MODE]);

  const login = () => {
    if (PUBLIC_MODE) return; // no-op in public mode
    keycloak?.login();
  };

  const logout = () => {
    if (PUBLIC_MODE) return; // no-op in public mode
    keycloak?.logout();
  };

  return (
    <AuthContext.Provider
      value={{
        keycloak,
        authenticated,
        initialized,
        login,
        logout,
        token: keycloak?.token || null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
